import { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import {CheckCheck,Diamond,LocateIcon, PhoneCall, LocateFixedIcon, Laptop, Headphones} from "lucide-react";
import { FaAndroid, FaApple, FaFacebookF, FaInstagram, FaTwitter, FaWhatsapp } from "react-icons/fa6";
import logo from "./assets/logo.png";

const OFFERS = [
  {
    icon: <FaApple/>,
    name: "iPhones",
    desc: "Latest models and certified pre-owned iPhones at unbeatable prices.",
  },
  {
    icon: <FaAndroid/>,
    name: "Android Phones",
    desc: "Samsung, OnePlus, Xiaomi, and more — new & used options available.",
  },
  {
    icon: <Laptop/>,
    name: "MacBooks & Tablets",
    desc: "Apple MacBooks, iPads, and Android tablets for every need.",
  },
  {
    icon: <Headphones/>,
    name: "Accessories",
    desc: "Cases, chargers, earbuds, and essential accessories for all devices.",
  },
];

const WHY_CARDS = [
  {
    num: "01",
    icon: <CheckCheck/>,
    title: "Trusted Devices",
    desc: "Every device is carefully inspected and verified before it reaches your hands. No surprises, only quality.",
  },
  {
    num: "02",
    icon: <Diamond/>,
    title: "Best Resale Value",
    desc: "We offer market-leading prices for your used devices. Upgrade smarter, earn more from your trade-in.",
  },
  {
    num: "03",
    icon: <LocateIcon/>,
    title: "Multiple Locations",
    desc: "With stores in Patiala, Nabha, Samana, and Delhi, we're always close to you. Visit us now to get best deals.",
  },
  {
    num: "04",
    icon: <PhoneCall/>,
    title: "Expert Support",
    desc: "Our trained team of mobile experts helps you find the right device and the best deal — every single time.",
  },
];

const LOCATIONS = [
  { city: "Patiala", state: "Punjab, India", tag: "Flagship" },
  { city: "Nabha", state: "Punjab, India", tag: "Store" },
  { city: "Samana", state: "Punjab, India", tag: "Store" },
  { city: "Delhi", state: "Delhi, India", tag: "Store" },
];

const SHOWCASE_ITEMS = [
  { visual: "phone", label: "iPhone 15 Pro", sub: "Apple · New & Used" },
  { visual: "macbook", label: "MacBook Pro", sub: "Apple Silicon · M3" },
  {
    visual: "phone",
    label: "Samsung Galaxy",
    sub: "Android · New & Used",
    bg: "linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)",
  },
  { visual: "accessory", label: "Accessories", sub: "Cases, Chargers & More" },
  {
    visual: "phone",
    label: "OnePlus 12",
    sub: "Android · Flagship",
    bg: "linear-gradient(135deg, #1a1f1a 0%, #0f1a0f 100%)",
  },
  { visual: "tablet", label: "iPad Pro", sub: "Apple · Tablet" },
  {
    visual: "phone",
    label: "iPhone 14",
    sub: "Apple · Pre-owned",
    bg: "linear-gradient(135deg, #1f1a1a 0%, #1a0f0f 100%)",
  },
  { visual: "earbud", label: "AirPods & Earbuds", sub: "Wireless Audio" },
];

const MARQUEE_ITEMS = [
  "iPhones",
  "Android Phones",
  "MacBooks",
  "Tablets",
  "Accessories",
  "Trade-In",
  "Best Prices",
  "4 Locations",
];
const LOC_ITEMS = ["Patiala", "Nabha", "Samana", "Delhi"];

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`avi-reveal${visible ? " visible" : ""}${delay ? ` avi-delay-${delay}` : ""}${className ? " " + className : ""}`}
    >
      {children}
    </div>
  );
}

function MarqueeBand({ items, reverse = false, dark = false }) {
  const doubled = [...items, ...items];
  return (
    <div
      className="avi-marquee-band"
      style={
        dark
          ? {
              background: "rgba(201,168,76,0.06)",
              borderTop: "1px solid rgba(201,168,76,0.1)",
              borderBottom: "1px solid rgba(201,168,76,0.1)",
            }
          : { background: "#c9a84c" }
      }
    >
      <div
        className={`avi-marquee-inner ${reverse ? "avi-marquee-reverse" : "avi-marquee-animate"}`}
      >
        {doubled.map((item, i) => (
          <div
            key={i}
            className="avi-marquee-item"
            style={
              dark ? { color: "rgba(201,168,76,0.7)" } : { color: "#080808" }
            }
          >
            {item}
            <span
              className="avi-marquee-dot"
              style={{
                background: dark ? "rgba(201,168,76,0.3)" : "rgba(8,8,8,0.35)",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ShowcaseVisual({ item }) {
  if (item.visual === "macbook") return <div className="avi-macbook-visual" />;
  if (item.visual === "accessory")
    return <div className="avi-accessory-visual" />;
  if (item.visual === "tablet")
    return (
      <div
        className="avi-macbook-visual"
        style={{
          background: "linear-gradient(135deg, #1a1a1a 0%, #141414 100%)",
          width: 120,
          height: 160,
          borderRadius: 16,
        }}
      />
    );
  if (item.visual === "earbud")
    return (
      <div
        className="avi-accessory-visual"
        style={{ borderRadius: 20, width: 100, height: 60 }}
      />
    );
  return (
    <div
      className="avi-phone-visual"
      style={item.bg ? { background: item.bg } : undefined}
    />
  );
}

function Nav({ scrolled }) {
  return (
    <nav
      className="avi-nav"
      style={{ background: scrolled ? "rgba(8,8,8,0.95)" : "rgba(8,8,8,0.75)" }}
    >
      <a href="#" className="avi-nav-logo">
        Avi<span>iStore</span>
      </a>
      <ul className="avi-nav-links">
        {[
          ["#offer", "What We Offer"],
          ["#buysell", "Buy & Sell"],
          ["#why", "Why Us"],
          ["#locations", "Locations"],
        ].map(([href, label]) => (
          <li key={href}>
            <a href={href}>{label}</a>
          </li>
        ))}
      </ul>
      <a href="#locations" className="avi-nav-cta">
        Find a Store
      </a>
    </nav>
  );
}

function Hero() {
  return (
    <section className="avi-hero">
      <div className="avi-hero-bg" />
      <div className="avi-hero-noise" />
      <div className="avi-float-devices" aria-hidden="true">
        <div className="avi-float-device avi-fd1" />
        <div className="avi-float-device avi-fd2" />
        <div className="avi-float-device avi-fd3" />
        <div className="avi-float-device avi-fd4" />
        <div className="avi-float-device avi-fd5" />
      </div>
      <div className="avi-hero-content">
        <span className="avi-hero-eyebrow">Trusted Mobile Retail</span>
        <h1 className="avi-hero-headline">
          Buy.
          <br />
          Sell.
          <br />
          Upgrade.
        </h1>
        <p className="avi-hero-sub">
          Premium mobile devices &amp; trade-in solutions.
          <br />
          <em>New, used, and certified — all in one place.</em>
        </p>
        <div className="avi-hero-btns">
          <a href="#locations" className="avi-btn-primary">
            Visit Our Stores →
          </a>
          <a href="#offer" className="avi-btn-ghost">
            Explore Devices
          </a>
        </div>
      </div>
      <div className="avi-hero-scroll" aria-hidden="true">
        <span className="avi-scroll-label">Scroll</span>
        <div className="avi-scroll-line" />
      </div>
    </section>
  );
}

function Offers() {
  return (
    <section id="offer">
      <div className="avi-section-inner">
        <Reveal>
          <span className="avi-section-label">Our Catalog</span>
        </Reveal>
        <Reveal delay={1}>
          <h2 className="avi-section-title">
            Everything mobile,
            <br />
            under one roof.
          </h2>
        </Reveal>
        <Reveal delay={2}>
          <p className="avi-section-desc">
            From the latest iPhones to certified pre-owned Androids — we stock
            what you need, at prices that make sense.
          </p>
        </Reveal>
        <div className="avi-offers-grid">
          {OFFERS.map((o, i) => (
            <Reveal key={o.name} delay={i + 1}>
              <div className="avi-offer-card">
                <div className="avi-offer-icon">{o.icon}</div>
                <div className="avi-offer-name">{o.name}</div>
                <p className="avi-offer-desc">{o.desc}</p>
                <span className="avi-offer-arrow">→</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function BuySell() {
  return (
    <section id="buysell" style={{ background: "var(--bg2)" }}>
      <div className="avi-section-inner">
        <Reveal>
          <span className="avi-section-label">Two Ways to Aviistore</span>
        </Reveal>
        <Reveal delay={1}>
          <h2 className="avi-section-title">
            Your device journey,
            <br />
            simplified.
          </h2>
        </Reveal>
        <div className="avi-bs-grid">
          {/* BUY */}
          <Reveal delay={2}>
            <div className="avi-bs-card avi-bs-buy">
              <div
                className="avi-bs-glow"
                style={{ background: "rgba(201,168,76,0.25)" }}
              />
              <span className="avi-bs-badge avi-bs-badge-buy">
                🛒 Buy Premium
              </span>
              <h3 className="avi-bs-title">
                Get the device
                <br />
                you deserve.
              </h3>
              <p className="avi-bs-desc">
                Browse a wide selection of new and certified pre-owned
                smartphones, MacBooks, and tablets — all quality-checked and
                ready to go.
              </p>
              <ul className="avi-bs-features">
                {[
                  "Quality-inspected devices",
                  "New & certified pre-owned stock",
                  "iPhones, Android, MacBooks & tablets",
                  "Expert guidance from our team",
                ].map((f) => (
                  <li key={f}>
                    <span
                      className="avi-bs-dot"
                      style={{ background: "var(--gold)" }}
                    />
                    {f}
                  </li>
                ))}
              </ul>
              <a href="#locations" className="avi-btn-gold">
                Shop In-Store →
              </a>
            </div>
          </Reveal>
          {/* SELL */}
          <Reveal delay={3}>
            <div className="avi-bs-card avi-bs-sell">
              <div
                className="avi-bs-glow"
                style={{ background: "rgba(100,160,255,0.2)" }}
              />
              <span className="avi-bs-badge avi-bs-badge-sell">
                💰 Sell / Trade-In
              </span>
              <h3 className="avi-bs-title">
                Turn your old phone
                <br />
                into cash.
              </h3>
              <p className="avi-bs-desc">
                Ready to upgrade? We buy your used smartphones and devices at
                the best resale value. Walk in, get assessed, get paid — it's
                that simple.
              </p>
              <ul className="avi-bs-features">
                {[
                  "Instant device assessment",
                  "Competitive trade-in prices",
                  "All brands & conditions accepted",
                  "Hassle-free experience",
                ].map((f) => (
                  <li key={f}>
                    <span
                      className="avi-bs-dot"
                      style={{ background: "#64a0ff" }}
                    />
                    {f}
                  </li>
                ))}
              </ul>
              <a href="#locations" className="avi-btn-blue">
                Sell Your Device →
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function WhyAviistore() {
  return (
    <section id="why">
      <div className="avi-section-inner">
        <Reveal>
          <span className="avi-section-label">Why Choose Us</span>
        </Reveal>
        <Reveal delay={1}>
          <h2 className="avi-section-title">
            The Aviistore
            <br />
            difference.
          </h2>
        </Reveal>
        <div className="avi-why-grid">
          {WHY_CARDS.map((w, i) => (
            <Reveal key={w.num} delay={i + 1}>
              <div className="avi-why-card">
                <span className="avi-why-number">{w.num}</span>
                <div className="avi-why-icon">{w.icon}</div>
                <div className="avi-why-title">{w.title}</div>
                <p className="avi-why-desc">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Locations() {
  return (
    <section id="locations" style={{ background: "var(--bg2)" }}>
      <div className="avi-section-inner">
        <Reveal>
          <span className="avi-section-label">Store Locations</span>
        </Reveal>
        <Reveal delay={1}>
          <h2 className="avi-section-title">We're near you.</h2>
        </Reveal>
        <Reveal delay={2}>
          <p className="avi-section-desc">
            Aviistore is present across four cities. Walk in, explore our
            collection, and let our experts guide you.
          </p>
        </Reveal>
        <div className="avi-loc-grid">
          {LOCATIONS.map((loc, i) => (
            <Reveal key={loc.city} delay={i + 1}>
              <div className="avi-loc-card">
                <div className="avi-loc-glow" />
                <div className="avi-loc-pin"><LocateFixedIcon/></div>
                <div className="avi-loc-city">{loc.city}</div>
                <div className="avi-loc-state">{loc.state}</div>
                <span className="avi-loc-tag">{loc.tag}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Showcase() {
  const doubled = [...SHOWCASE_ITEMS, ...SHOWCASE_ITEMS];
  return (
    <section id="showcase">
      <div className="avi-section-inner" style={{ paddingBottom: 40 }}>
        <Reveal>
          <span className="avi-section-label">Brand Showcase</span>
        </Reveal>
        <Reveal delay={1}>
          <h2 className="avi-section-title">
            Premium gear,
            <br />
            every category.
          </h2>
        </Reveal>
      </div>
      <Reveal>
        <div className="avi-showcase-wrap">
          <div className="avi-showcase-track">
            {doubled.map((item, i) => (
              <div className="avi-showcase-item" key={i}>
                <div className="avi-si-glow" />
                <ShowcaseVisual item={item} />
                <div style={{ textAlign: "center" }}>
                  <div className="avi-showcase-label">{item.label}</div>
                  <div className="avi-showcase-sublabel">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
      <div style={{ height: 80 }} />
    </section>
  );
}

function CTA() {
  return (
    <section className="avi-cta">
      <div className="avi-section-inner" style={{ padding: "140px 48px" }}>
        <Reveal>
          <h2 className="avi-cta-title">
            Visit Aviistore
            <br />
            Today.
          </h2>
          <p className="avi-cta-sub">
            Your next device is waiting. Walk in, explore, and leave happy.
          </p>
          <div className="avi-cta-btns">
            <a href="tel:+91" className="avi-btn-primary">
              <PhoneCall/> Call Us
            </a>
            <a href="#locations" className="avi-btn-ghost">
              <LocateFixedIcon/> Get Directions
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  const cols = [
    {
      heading: "Devices",
      links: [
        "iPhones",
        "Android Phones",
        "MacBooks",
        "Tablets",
        "Accessories",
      ],
    },
    {
      heading: "Services",
      links: ["Buy a Device", "Sell Your Phone", "Trade-In", "Device Check"],
    },
    { heading: "Locations", links: ["Patiala", "Nabha", "Samana", "Delhi"] },
  ];
  return (
    <footer className="avi-footer">
      <div className="avi-footer-inner">
        <div className="avi-footer-top">
          <div className="avi-footer-brand">
            <img src={logo} alt="logo" className="mylogo" />
            <span className="avi-footer-brand-name">
              Avi<span>iStore</span>
            </span>
            <p>
              Your trusted destination for premium mobile devices and smart
              trade-in solutions across Punjab &amp; Delhi.
            </p>
          </div>
          {cols.map((col) => (
            <div className="avi-footer-col" key={col.heading}>
              <h4>{col.heading}</h4>
              <ul>
                {col.links.map((l) => (
                  <li key={l}>
                    <a href="#">{l}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="avi-footer-bottom">
          <span className="avi-footer-copy">
            &copy; 2026 Aviistore. All rights reserved. Premium Mobile Retail.
          </span>
          <div className="avi-footer-socials">
            {[
              [<FaInstagram/>, "Instagram"],
              [<FaFacebookF/>, "Facebook"],
              [<FaWhatsapp/>, "WhatsApp"],
              [<FaTwitter/>, "Twitter"],
            ].map(([icon, label]) => (
              <a href="#" className="avi-social-btn" title={label} key={label}>
                {icon}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Nav scrolled={scrolled} />
      <Hero />
      <MarqueeBand items={MARQUEE_ITEMS} />
      <Offers />
      <div className="avi-divider" />
      <BuySell />
      <div className="avi-divider" />
      <WhyAviistore />
      <MarqueeBand items={LOC_ITEMS} reverse dark />
      <Locations />
      <div className="avi-divider" />
      <Showcase />
      <CTA />
      <Footer />
    </>
  );
}
